/**
 * Turning the Loyalty Profile block's authored text into what the membership dialogs take.
 *
 * Three shapes have to be built here rather than in PHP, and each for its own reason:
 *
 * - **The consequences list.** `ManageLoyaltyModal` keys each `<li>` BY THE LINE'S OWN TEXT, so two
 *   identical bullets are one key twice -- which Preact diffs wrongly and reports nowhere.
 * - **The withdrawal notice.** The library takes an array of runs rather than a string or a
 *   `ReactNode`, so that word order stays the host's and the whole thing survives being stored in a
 *   database. An editor writes `[label](target)`; this is where that becomes runs.
 * - **The leave paragraph.** It names the fan's own figures, which only the browser has.
 *
 * **Nothing is imported here.** That is what lets membershipCopy.test.js drive every rule below
 * under `node --test`, which cannot load the library at all -- every one of its export paths
 * transitively pulls in a `.css` file.
 *
 * The authoring rules are not preferences. Each one is a property of the library's own renderer,
 * read off the built source, and each is noted at the function that enforces it.
 */

/** `[visible text](target)`. A target containing a `)` is not expressible; see parseRichText. */
const LINK_PATTERN = /\[([^\]]*)\]\(([^)]*)\)/g;

/** `{points}`, `{tier}`, `{badges}` — and anything else, which is how an unknown one is caught. */
const TOKEN_PATTERN = /\{([a-zA-Z]+)\}/g;

/**
 * One bullet per line, ready to be handed to `ManageLoyaltyModal.consequences`.
 *
 * Blank lines go, because an editor separating bullets with a blank line means nothing by it.
 *
 * **Exact duplicates go too, and that is the rule worth knowing.** The modal renders each line as
 * `<li key={line}>` -- the text IS the key -- so two identical bullets give Preact one key twice.
 * It does not throw, does not warn, and mis-diffs the list quietly. Dropping the repeat is the only
 * outcome that is both safe and what the editor visibly meant.
 *
 * @param {string} text The textarea's contents.
 * @return {string[]} The bullets, in order, first occurrence wins.
 */
export function splitConsequences( text ) {
	if ( typeof text !== 'string' ) {
		return [];
	}

	const seen = new Set();

	return text
		.split( '\n' )
		.map( line => line.trim() )
		.filter( line => {
			if ( line === '' || seen.has( line ) ) {
				return false;
			}

			seen.add( line );

			return true;
		} );
}

/**
 * One paragraph with links inside it, as the runs `RichText` draws.
 *
 * **Runs are concatenated with NOTHING between them**, so every space and every full stop belongs
 * inside a run. Nothing here trims: trimming a text run would silently delete the space between it
 * and the link beside it, and the sentence would close up.
 *
 * **A link run carries `href` only when there is one to carry.** `RichText` branches on
 * `segment.href !== undefined`, not on whether it is empty -- so a run built with `href: ''` renders
 * a live `<a href="">` that navigates the fan to the page they are standing on. An absent key is
 * what makes the phrase render as plain text, which is the intended degradation and what the
 * shipped wording relies on.
 *
 * Two shapes are deliberately left as literal text rather than guessed at: a `[label]` with no
 * `(target)` after it, and a `[](target)` with no label. The first is an editor who has not finished
 * typing; the second would be an anchor with nothing to click.
 *
 * A target containing a closing parenthesis cannot be expressed -- the scan stops at the first one.
 * Percent-encode it (`%29`), which every browser resolves.
 *
 * @param {string} text The textarea's contents.
 * @return {Array<Object>} Runs, in order. Empty when there is nothing to draw.
 */
export function parseRichText( text ) {
	if ( typeof text !== 'string' || text === '' ) {
		return [];
	}

	const segments = [];
	let cursor = 0;

	// A fresh regex per call: LINK_PATTERN is global, and a shared one carries lastIndex between
	// calls, so the second caller would start scanning from wherever the first stopped.
	const pattern = new RegExp( LINK_PATTERN.source, 'g' );
	let match = pattern.exec( text );

	while ( match !== null ) {
		const [ whole, label, target ] = match;

		if ( label === '' ) {
			// No label, so no anchor. Left exactly as typed, including its brackets, so an editor
			// can see what they wrote rather than watching it disappear.
			match = pattern.exec( text );
			continue;
		}

		if ( match.index > cursor ) {
			segments.push( { kind: 'text', text: text.slice( cursor, match.index ) } );
		}

		const link = { kind: 'link', text: label };
		const href = target.trim();

		// The key is OMITTED, never set to ''. See the docblock.
		if ( href !== '' ) {
			link.href = href;
		}

		segments.push( link );
		cursor = match.index + whole.length;
		match = pattern.exec( text );
	}

	if ( cursor < text.length ) {
		segments.push( { kind: 'text', text: text.slice( cursor ) } );
	}

	return segments;
}

/**
 * The leave dialog's paragraph, with the fan's own figures in it.
 *
 * **Interpolation is total: either every token is filled, or the plain sentence is used instead.**
 * There is no partial result, and that is the whole point. A fan reading a sentence about leaving
 * must never meet a literal `{points}`, and must never meet a sentence whose numbers we could not
 * read -- both of which are worse than a correct sentence carrying no numbers at all. So an unknown
 * token, a missing value and a blank template all take the same exit.
 *
 * @param {string} template The authored sentence, with `{points}` / `{tier}` / `{badges}`.
 * @param {string} plain    The same sentence without figures, used whenever this cannot be filled.
 * @param {Object} values   What each token resolves to. A missing or blank entry is a miss.
 * @return {string} One of the two sentences, never a mixture.
 */
export function interpolate( template, plain, values ) {
	const fallback = typeof plain === 'string' ? plain : '';

	if ( typeof template !== 'string' || template === '' ) {
		return fallback;
	}

	let usable = true;

	const filled = template.replace( TOKEN_PATTERN, ( token, name ) => {
		const value = values && values[ name ];

		if ( typeof value !== 'string' || value === '' ) {
			usable = false;

			return token;
		}

		return value;
	} );

	return usable ? filled : fallback;
}

/**
 * The membership card's points run, which the library takes already formatted.
 *
 * The library draws the `·` between this and the tier name and nothing else -- the number, its
 * separators and its unit are all the host's, which is why `formatNumber` comes from
 * `@drewling/urc-loyalty-api` at the call site rather than being reimplemented anywhere.
 *
 * @param {string} points A formatted number, e.g. '1,160'.
 * @param {string} suffix The authored unit, e.g. 'PTS'. Blank leaves the number alone.
 * @return {string} e.g. '1,160 PTS'.
 */
export function formatPoints( points, suffix ) {
	const unit = typeof suffix === 'string' ? suffix.trim() : '';

	return unit === '' ? points : `${ points } ${ unit }`;
}

/**
 * The membership card's date, as the fan reads it: `'12 Oct 2025'`, or blank when there is none.
 *
 * **Both arguments to `Intl` are pinned, and each pin is load-bearing rather than tidy.**
 *
 * The LOCALE, because an unpinned one follows the MACHINE and not the site: the same instant
 * renders `21 сент. 2026 г.` on a Russian browser, inside an English dialog whose every other word
 * came from the editor. `en-GB` is what the theme's one other browser-side date formatter pins
 * (../partials/fixtures-async.js) and what the library pins throughout.
 *
 * The TIME ZONE, because this is a record stamp rather than a moment in the fan's day, and because
 * without it the DAY moves: `2026-09-21T23:30:00Z` renders `21 Sept 2026` in UTC and `22 Sept 2026`
 * in Sydney, measured. UTC is also the only reading a test can pin and the one an admin reading the
 * provider's own record sees. The named cost: a fan who joined within an hour of local midnight
 * under BST may be shown the previous day. `Europe/London` is the one-word alternative if that
 * boundary ever matters more than agreement between fans.
 *
 * Expect `Sept`, not `Sep` -- `en-GB`'s CLDR short form for September is four letters, while every
 * other month is three. It is pinned in the tests deliberately, so a CLDR change is a visible
 * failure rather than a silent restyling.
 *
 * The formatter is built per call. This file's whole value is being stateless and import-free, and
 * a module-level singleton would trade that for one construction per dialog open.
 *
 * @param {string} iso An ISO-8601 instant, e.g. `LoyaltyProfile.accountCreatedAt`. Anything else --
 *                     null, undefined, a blank, a non-string, an unparseable date -- is a miss.
 * @return {string} The formatted date, or '' when there is nothing to format.
 */
export function formatJoinDate( iso ) {
	if ( typeof iso !== 'string' || iso.trim() === '' ) {
		return '';
	}

	const date = new Date( iso );

	// Never an Invalid Date onto the card: `toLocaleDateString` would render the literal
	// "Invalid Date", and Intl.format throws a RangeError. Both are worse than no line.
	if ( Number.isNaN( date.getTime() ) ) {
		return '';
	}

	return new Intl.DateTimeFormat( 'en-GB', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		timeZone: 'UTC',
	} ).format( date );
}

/**
 * The membership card's "Member since …" line, or nothing at all.
 *
 * **It answers `undefined` and never `''`, and that is a property of the library's renderer rather
 * than a style.** `MembershipCard` branches on `membership.memberSince === undefined` -- the same
 * shape `RichText` uses for `href` -- and its `.copy` is a flex column with a gap, so a blank
 * string ships an invisible flex item AND its 16px gap: the card grows and the fan sees a ragged
 * space under the headline. The key has to be absent, not empty.
 *
 * Interpolation is `interpolate`'s, with `''` for the plain fallback -- which is how "and here,
 * the fallback is nothing at all" is said in this file's existing vocabulary. A blank template, an
 * unknown token, and a date we could not read all take the same exit, and a literal `{date}` can
 * never reach a fan.
 *
 * **The early return is the one thing a bare `interpolate` call would get wrong.** `interpolate`
 * returns a token-free sentence unchanged, so an editor who wrote `Member since the start` with no
 * `{date}` in it would keep printing that standing claim on a fan whose date failed to read. The
 * date is checked first, so no date means no line whatever the template says.
 *
 * @param {string} template The authored line, with `{date}` in it.
 * @param {string} iso      The instant, as `formatJoinDate` takes it.
 * @return {string|undefined} The line, or undefined when it must not be drawn.
 */
export function formatMemberSince( template, iso ) {
	const date = formatJoinDate( iso );

	if ( date === '' ) {
		return undefined;
	}

	const line = interpolate( template, '', { date } );

	return line === '' ? undefined : line;
}
